// Same logic as landing and v2-frontend authGuard
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getV2Token } from '../utils/v2AuthUtils';
import SecureCookieManager from '../utils/secureCookie';
import { validateToken } from '../utils/tokenValidator';
import { clearV2Token } from '../utils/v2AuthUtils';
import { isDemoUser } from '../utils/demoAuth';
// Subscribe & Zoho subscribe - commented out
// import { useSubscription } from '../context/SubscriptionContext';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  // Subscribe & Zoho subscribe - commented out
  // const { status: subscriptionStatus, loading: subscriptionLoading } = useSubscription();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = getV2Token();
        
        // Allow access to login, error, and subscription pages (same as landing and v2-frontend)
        if (location.pathname === '/login' || location.pathname.startsWith('/error') || 
            // location.pathname === '/subscription' || location.pathname === '/renew-subscription' ||
            location.pathname === '/register') {
          setIsLoading(false);
          return;
        }

        if (!token) {
          if (location.pathname === '/login' || location.pathname.startsWith('/error') || 
              // location.pathname === '/subscription' || location.pathname === '/renew-subscription' ||
              location.pathname === '/register') {
            setIsLoading(false);
            return;
          }
          window.location.href = '/login';
          return;
        }

        // Validate token locally (same as landing and v2-frontend - client-side validation)
        const userData = validateToken(token);
        if (!userData) {
          // Token invalid or expired, clear it and go direct to login
          const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
          clearV2Token(domain);
          SecureCookieManager.clearAuthCookies();
          window.location.href = '/login';
          return;
        }

        // Token is valid, store it in front_compliance's token cookie for backward compatibility
        SecureCookieManager.setAuthToken(token);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error checking authentication:', error);
        // On error, clear token and go direct to login
        const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
        clearV2Token(domain);
        SecureCookieManager.clearAuthCookies();
        window.location.href = '/login';
        return;
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]);

  if (isLoading) {
    return <div>Loading...</div>; // Same simple loading as landing and v2-frontend
  }

  // Allow access to login, error, and subscription pages (same as landing and v2-frontend)
  if (location.pathname === '/login' || location.pathname.startsWith('/error') || 
      // location.pathname === '/subscription' || location.pathname === '/renew-subscription' ||
      location.pathname === '/register') {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    // Full redirect so auth state is fully cleared and user lands on login
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  // Demo user: skip subscription check so they can see demo pages
  const token = getV2Token();
  if (token && isDemoUser(token)) {
    return <>{children}</>;
  }

  // Subscribe & Zoho subscribe - commented out: subscription check no longer blocks access
  /*
  const isSubscriptionPage = location.pathname === '/subscription' || location.pathname === '/renew-subscription';
  
  if (!isSubscriptionPage && !subscriptionLoading) {
    if (!subscriptionStatus || subscriptionStatus === 'error' || subscriptionStatus === 'suspended' || subscriptionStatus === 'canceled') {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '70vh', padding: '2rem' }}>
          <div className="text-center" style={{ maxWidth: '600px' }}>
            <h4 className="mb-3">
              {!subscriptionStatus || subscriptionStatus === 'error'
                ? t("header.No Subscription") || "No Active Subscription"
                : subscriptionStatus === 'suspended' 
                ? t("header.Subscription Suspended") || "Subscription Suspended"
                : t("header.Subscription Stopped") || "Subscription Stopped"}
            </h4>
            <p className="text-muted mb-4">
              {!subscriptionStatus || subscriptionStatus === 'error'
                ? t("header.You don't have an active subscription. Please subscribe to continue using our services.") || 
                  "You don't have an active subscription. Please subscribe to continue using our services."
                : subscriptionStatus === 'suspended'
                ? t("header.Your subscription has been suspended. Please renew to continue using our services.") || 
                  "Your subscription has been suspended. Please renew to continue using our services."
                : t("header.Your subscription has been stopped. Please renew to continue using our services.") || 
                  "Your subscription has been stopped. Please renew to continue using our services."}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { window.location.href = !subscriptionStatus || subscriptionStatus === 'error' ? '/subscription' : '/renew-subscription'; }}
              style={{ padding: '10px 30px', fontSize: '16px' }}
            >
              {!subscriptionStatus || subscriptionStatus === 'error'
                ? t("header.Subscribe Now") || "Subscribe Now"
                : t("header.Renew Subscription") || "Renew Subscription"}
            </button>
          </div>
        </div>
      );
    }
  }
  */

  return <>{children}</>;
};

export default ProtectedRoute; 